import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";

/* eslint-disable no-unused-vars */

export default function CopyDayDialog({ fromDate, weekDays, entries, onClose, onSaved }) {
  const [toDate, setToDate] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customDate, setCustomDate] = useState("");

  const submit = async () => {
    const finalTo = toDate || customDate;
    if (!finalTo) { toast.error("Selectează o zi destinație"); return; }
    if (finalTo === fromDate) { toast.error("Sursa și destinația sunt aceeași zi"); return; }
    setBusy(true);
    try {
      const res = await api.post("/planificator/copy-day", { from_date: fromDate, to_date: finalTo, overwrite });
      const { copied, merged, skipped, deleted } = res.data || {};
      const parts = [];
      if (copied) parts.push(`${copied} copiate`);
      if (merged) parts.push(`${merged} comasate`);
      if (skipped) parts.push(`${skipped} ignorate (proiect inactiv)`);
      if (deleted) parts.push(`${deleted} șterse`);
      toast.success(parts.length ? parts.join(" · ") : "Nicio modificare");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la copiere");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays size={18} className="text-[var(--primary)]"/>
            Copiază alocările din <strong>{fromDate}</strong>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2">
            <strong>{entries.length}</strong> {entries.length === 1 ? "alocare" : "alocări"} de copiat:{" "}
            {entries.slice(0, 3).map((e) => e.label).join(", ")}{entries.length > 3 ? ` +${entries.length - 3}` : ""}
          </div>

          <div>
            <Label className="small-label">Destinație — alege zi din săptămâna curentă</Label>
            <div className="grid grid-cols-7 gap-1 mt-1.5">
              {weekDays.map((d) => {
                const isSrc = d === fromDate;
                const isSel = d === toDate;
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={isSrc}
                    onClick={() => { setToDate(d); setCustomDate(""); }}
                    className={`text-[10px] py-2 rounded border transition-colors ${
                      isSrc ? "bg-zinc-100 text-zinc-400 cursor-not-allowed border-zinc-200"
                      : isSel ? "bg-[var(--primary)] text-white border-[var(--primary)] font-bold"
                      : "border-zinc-300 hover:bg-zinc-50"
                    }`}
                    data-testid={`copy-target-${d}`}
                  >
                    <div>{d.slice(8, 10)}</div>
                    <div className="text-[8px] opacity-70">{d.slice(5, 7)}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="small-label">…sau scrie altă dată</Label>
            <Input
              type="date"
              value={customDate}
              onChange={(e) => { setCustomDate(e.target.value); setToDate(""); }}
              className="mt-1"
              data-testid="copy-custom-date"
            />
          </div>

          <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-amber-200 bg-amber-50">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="mt-0.5"
              data-testid="copy-overwrite"
            />
            <span>
              <strong className="block">Suprascrie alocările existente pe ziua destinație</strong>
              <span className="text-zinc-600">Default: alocările existente se păstrează și se face merge pe persoane/mașini.</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={busy || (!toDate && !customDate)} className="!text-red-600 font-bold" data-testid="copy-confirm">
            {busy ? "Se copiază…" : "Copiază"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
